import { useState, useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import { bulkEmailApi } from "@/apis/bulk-email";
import { SimpleCsvParser } from "@/organism/BulkEmail/CsvDataTable/simpleCsvParser";
import { type CsvData } from "@/organism/BulkEmail/CsvDataTable/useCsvData";

/**
 * 발송 결과 인터페이스
 */
interface SendResults {
  total: number;
  successful: number;
  failed: number;
  errors?: Array<{
    email: string;
    error: string;
  }>;
}

/**
 * 이메일 대량 발송 페이지 커스텀 훅
 * @description CSV 파일 업로드, 파싱, 대량 이메일 발송 로직을 관리합니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-15
 */
export default function useList() {
  // 상태 관리
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<CsvData[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [sendResults, setSendResults] = useState<SendResults | null>(null);
  const [selectedDoJangId, setSelectedDoJangId] = useState<string>("");
  const [selectedDoJangName, setSelectedDoJangName] = useState<string>("");

  /**
   * CSV 파일 파싱 함수 (개선된 버전)
   * @param file CSV 파일
   * @returns 파싱된 데이터 배열
   */
  const parseCsvFile = useCallback(async (file: File): Promise<CsvData[]> => {
    const result = await SimpleCsvParser.parseFile(file);
    
    console.log('[CSV_PARSE_RESULT]', {
      success: result.success,
      validRecords: result.data.length,
      totalLines: result.totalLines,
      errorCount: result.errors.length,
      errors: result.errors.slice(0, 3) // 처음 3개 에러만 로그
    });

    if (!result.success) {
      // 에러가 있어도 유효한 데이터가 있으면 경고와 함께 진행
      if (result.data.length > 0) {
        const errorSummary = result.errors.length > 3 
          ? `${result.errors.slice(0, 3).join(', ')} 및 ${result.errors.length - 3}개 추가 오류`
          : result.errors.join(', ');
        
        enqueueSnackbar(
          `CSV 파일에 ${result.errors.length}개 오류가 있습니다. 유효한 ${result.data.length}개 레코드만 처리됩니다.`,
          { variant: "warning" }
        );
        
        console.warn('[CSV_PARTIAL_SUCCESS]', {
          errorSummary,
          validRecords: result.data.length
        });
        
        return result.data;
      } else {
        // 유효한 데이터가 없으면 에러
        const firstError = result.errors[0] || "Unknown parsing error";
        throw new Error(firstError);
      }
    }

    // 성공적으로 파싱된 경우에도 경고나 알림이 있으면 표시
    if (result.errors.length > 0) {
      // 중복 제거 메시지와 일반 에러 메시지 분리
      const duplicateMessages = result.errors.filter(error => error.includes('중복된 이메일'));
      const otherErrors = result.errors.filter(error => !error.includes('중복된 이메일'));
      
      if (duplicateMessages.length > 0) {
        // 중복 제거 성공 알림 (정보성)
        enqueueSnackbar(
          duplicateMessages[0], // "✅ 중복된 이메일 X개가 자동으로 제거되었습니다..."
          { variant: "info" }
        );
      }
      
      if (otherErrors.length > 0) {
        // 기타 문제들에 대한 경고
        enqueueSnackbar(
          `일부 행에서 ${otherErrors.length}개 문제가 발견되었지만 ${result.data.length}개 레코드가 성공적으로 파싱되었습니다.`,
          { variant: "warning" }
        );
      }
    }

    return result.data;
  }, []);

  /**
   * 파일 업로드 핸들러
   * @param event 파일 입력 이벤트
   */
  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // 파일 존재 검증
      if (!file || file.size === 0) {
        enqueueSnackbar("유효하지 않은 파일입니다.", { variant: "error" });
        return;
      }

      // 파일 타입 검증 (더 엄격하게)
      const fileName = file.name.toLowerCase();
      const fileType = file.type.toLowerCase();
      
      if (!fileName.endsWith(".csv") && !fileType.includes("csv") && !fileType.includes("text")) {
        enqueueSnackbar("CSV 파일만 업로드 가능합니다. (.csv 확장자)", { variant: "error" });
        return;
      }

      // 파일 크기 검증 (10MB 제한)
      if (file.size > 10 * 1024 * 1024) {
        enqueueSnackbar("파일 크기가 너무 큽니다. (최대 10MB)", {
          variant: "error",
        });
        return;
      }

      // 파일 크기가 너무 작은 경우 검증
      if (file.size < 10) {
        enqueueSnackbar("파일이 너무 작습니다. 유효한 CSV 데이터가 있는지 확인해주세요.", {
          variant: "error",
        });
        return;
      }

      setIsUploading(true);
      setSendResults(null); // 이전 결과 초기화

      try {
        console.log("[CSV_UPLOAD]", {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        });

        const parsedData = await parseCsvFile(file);

        console.log("[CSV_PARSED]", {
          totalRecords: parsedData.length,
          sampleData: parsedData.slice(0, 3),
        });

        setCsvFile(file);
        setCsvData(parsedData);

        enqueueSnackbar(
          `CSV 파일이 성공적으로 업로드되었습니다. (${parsedData.length}명)`,
          { variant: "success" }
        );
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error("[CSV_UPLOAD_ERROR]", {
          error,
          errorMessage,
          fileName: file.name,
          fileSize: file.size,
          stack: error instanceof Error ? error.stack : undefined
        });
        enqueueSnackbar(`CSV 파일 처리 오류: ${errorMessage}`, {
          variant: "error",
        });
        setCsvFile(null);
        setCsvData([]);
      } finally {
        setIsUploading(false);
        // 파일 input 초기화
        event.target.value = "";
      }
    },
    [parseCsvFile]
  );

  /**
   * 파일 제거 핸들러
   */
  const handleFileRemove = useCallback(() => {
    setCsvFile(null);
    setCsvData([]);
    setSendResults(null);
    enqueueSnackbar("파일이 제거되었습니다.", { variant: "info" });
  }, []);

  /**
   * 도장 선택 핸들러
   */
  const handleDoJangChange = useCallback(
    (doJangId: string, doJangName: string) => {
      setSelectedDoJangId(doJangId);
      setSelectedDoJangName(doJangName);
      console.log("[DOJANG_SELECTED]", { doJangId, doJangName });
    },
    []
  );

  /**
   * 이메일 대량 발송 핸들러
   */
  const handleSendEmails = useCallback(async () => {
    if (!csvData.length || !selectedDoJangId) {
      enqueueSnackbar("CSV 파일과 도장을 선택해주세요.", {
        variant: "error",
      });
      return;
    }

    setIsSending(true);
    setSendProgress(0);
    setSendResults(null);

    try {
      console.log("[BULK_EMAIL_START]", {
        selectedDoJangId,
        selectedDoJangName,
        totalEmails: csvData.length,
        emails: csvData.map((d) => d.email),
      });

      // 진행률 시뮬레이션 (실제로는 API에서 진행률을 받아와야 함)
      const progressInterval = setInterval(() => {
        setSendProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      // API 호출 - 현재 API 스펙에 맞춰 이메일 리스트로 전송
      const emails = csvData.map((data) => data.email);

      console.log('[API_CALL_DATA]', {
        emailCount: emails.length,
        sampleEmails: emails.slice(0, 3),
        companyId: Number(selectedDoJangId),
        // 나중에 멤버 전체 정보를 전송할 수 있도록 주석으로 보관
        // memberData: csvData.slice(0, 2)
      });

      const result = await bulkEmailApi.sendTempPasswords({
        emails, // 현재 API 스펙: 이메일 리스트만 전송
        companyId: Number(selectedDoJangId),
        companyName: selectedDoJangName,
      });

      clearInterval(progressInterval);
      setSendProgress(100);

      console.log("[BULK_EMAIL_SUCCESS]", result);

      setSendResults({
        total: result.total || csvData.length,
        successful: result.successful || 0,
        failed: result.failed || 0,
        errors: result.errors || [],
      });

      enqueueSnackbar(
        `${selectedDoJangName}에 이메일 발송 완료! 성공: ${result.successful}명, 실패: ${result.failed}명`,
        { variant: "success" }
      );
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error("[BULK_EMAIL_ERROR]", {
        error,
        errorMessage,
        csvDataLength: csvData.length,
        selectedDoJangId,
        stack: error instanceof Error ? error.stack : undefined,
        fullError: error
      });

      // 백엔드에서 실제 발송 결과를 확인해보기
      try {
        console.log("[CHECKING_ACTUAL_RESULTS]", "백엔드 로그 확인 중...");
        
        // 실제 발송이 성공했을 수도 있으므로, 로그를 확인
        const logData = await bulkEmailApi.getRecentEmailLogs(Number(selectedDoJangId), 20);
        
        if (logData.logs.length > 0) {
          const recentLogs = logData.logs.filter(log => {
            const logTime = new Date(log.regDttm);
            const now = new Date();
            return (now.getTime() - logTime.getTime()) < 60000; // 1분 이내
          });
          
          if (recentLogs.length > 0) {
            const successCount = recentLogs.filter(log => log.sendStatus === 'SUCCESS').length;
            const failedCount = recentLogs.filter(log => log.sendStatus === 'FAILED').length;
            
            console.log("[ACTUAL_RESULTS_FOUND]", {
              successCount,
              failedCount,
              totalLogs: recentLogs.length
            });
            
            setSendResults({
              total: recentLogs.length,
              successful: successCount,
              failed: failedCount,
              errors: recentLogs
                .filter(log => log.sendStatus === 'FAILED')
                .map(log => ({ email: log.email, error: log.errorMessage || 'Unknown error' })),
            });
            
            enqueueSnackbar(
              `실제 발송 결과: 성공 ${successCount}명, 실패 ${failedCount}명 (API 응답 오류 발생했지만 실제 발송은 처리됨)`,
              { variant: successCount > 0 ? "warning" : "error" }
            );
            
            return; // 실제 결과를 찾았으므로 여기서 종료
          }
        }
      } catch (logError) {
        console.error("[LOG_CHECK_ERROR]", logError);
      }

      // 실제 로그를 찾지 못한 경우에만 실패로 처리
      setSendResults({
        total: csvData.length,
        successful: 0,
        failed: csvData.length,
        errors: [{ email: "all", error: errorMessage }],
      });

      enqueueSnackbar(`이메일 발송 실패: ${errorMessage}`, {
        variant: "error",
      });
    } finally {
      setIsSending(false);
    }
  }, [csvData, selectedDoJangId, selectedDoJangName]);

  return {
    // 상태
    csvFile,
    csvData,
    isUploading,
    isSending,
    sendProgress,
    sendResults,
    selectedDoJangId,
    selectedDoJangName,

    // 핸들러
    handleFileUpload,
    handleSendEmails,
    handleFileRemove,
    handleDoJangChange,
  };
}
